import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { DateTimeValue, QuoteData } from "~types/types";

/* eslint-disable @typescript-eslint/no-var-requires */
const {
  preset: marketsPreset,
} = require("@bloomberg/media-ui-markets/markets-preset");
/* eslint-enable @typescript-eslint/no-var-requires */

const chartColors = marketsPreset.theme.extend.colors;

export const compareColors: string[] = [
  "#000000",
  chartColors["chart-blue"],
  chartColors["chart-orange"],
  chartColors["chart-purple"],
  chartColors["chart-sky-blue"],
  chartColors["chart-red"],
  chartColors["chart-mustard"],
  chartColors["chart-pink"],
  chartColors["chart-green"],
  chartColors["chart-brown"],
];

export interface TickerData {
  id: string;
  color: string;
  type: string;
  previousPrice?: number;
  seriesEndTimestamp?: number;
  issuedCurrency: string | null;
  issuedUnits: string | null;
  priceMovements1Day?: DateTimeValue[];
  priceMovements1Month?: DateTimeValue[];
  priceMovements6Months?: DateTimeValue[];
  priceMovementsYearToDate?: DateTimeValue[];
  priceMovements1Year?: DateTimeValue[];
  priceMovements5Years?: DateTimeValue[];
  tradingDayClose?: string;
}

export type QuoteCompareSecurity = {
  tickerId: string;
  color: string;
};

interface QuoteCompareContextType {
  quotes: TickerData[];
  addQuote: (id: string) => void;
  clearComparedQuotes: () => void;
  removeQuoteById: (quoteId: string) => void;
  requestStatus?: {
    status: "pending" | "done" | "error";
    error?: string;
  };
}

const fetchTickerData = async (tickers: Pick<TickerData, "id" | "color">[]) => {
  // Sorting tickers so that similar query combinations can be cached.
  // Ex. ?tickers=AAPL:US,ARM:US and ?tickers=ARM:US,AAPL:US will hit the same cache
  const query = tickers
    .map(({ id }) => id)
    .sort()
    .join(",");

  const res = await fetch(
    `/bazaar/api/snapshot-charts?tickers=${query}&primaryTicker=${tickers[0].id}`
  );

  const data = await res.json();

  const tickerMap = data.reduce(
    (acc: Record<string, TickerData>, quoteData: QuoteData) => {
      // eslint-disable-next-line no-param-reassign
      acc[quoteData.id] = {
        ...quoteData,
        previousPrice:
          quoteData?.previousClosingPriceOneTradingDayAgo || undefined,
        color:
          tickers.find(({ id }) => id === quoteData.id)?.color ||
          compareColors[0],
      };

      return acc;
    },
    {}
  );

  return tickers.map(({ id }: { id: string }) => {
    return tickerMap[id];
  });
};

const QuoteCompareContext = createContext<QuoteCompareContextType | undefined>(
  undefined
);

export const useQuoteCompare = (): QuoteCompareContextType => {
  const context = useContext(QuoteCompareContext);

  if (!context) {
    throw new Error(
      "useQuoteCompare must be used within a QuoteCompareProvider"
    );
  }

  return context;
};

interface QuoteCompareProviderProps {
  children: React.ReactNode;
  initialQuote: QuoteData;
}

const QuoteCompareProvider: React.FC<QuoteCompareProviderProps> = ({
  children,
  initialQuote,
}) => {
  const [quotes, setQuotes] = useState<TickerData[]>([
    {
      ...initialQuote,
      color: compareColors[0],
      id: initialQuote.id.toUpperCase(),
      previousPrice:
        initialQuote.previousClosingPriceOneTradingDayAgo || undefined,
    },
  ]);

  const [requestStatus, setRequestStatus] =
    useState<QuoteCompareContextType["requestStatus"]>();

  const availableColors = useMemo(() => {
    return compareColors.filter(
      (color) => !quotes.some(({ color: tickerColor }) => color === tickerColor)
    );
  }, [quotes]);

  const addQuote = useCallback(
    async (tickerId: string) => {
      const isDuplicate = quotes.some(({ id }) => id === tickerId);

      if (isDuplicate) {
        return;
      }

      setRequestStatus({ status: "pending" });

      const tickersToFetch = quotes.map(({ id, color }) => ({ id, color }));

      tickersToFetch.push({ id: tickerId, color: availableColors[0] });

      try {
        const tickerData = await fetchTickerData(tickersToFetch);

        setQuotes(tickerData);
        setRequestStatus({ status: "done" });
      } catch (e) {
        setRequestStatus({
          status: "error",
          error: e instanceof Error ? e.message : JSON.stringify(e),
        });
      }
    },
    [availableColors, quotes]
  );

  const removeQuoteById = useCallback(
    (id: string) => {
      setQuotes(quotes.filter(({ id: quoteId }) => quoteId !== id));
    },
    [quotes]
  );

  const clearComparedQuotes = useCallback(() => {
    setQuotes([quotes[0]]);
  }, [quotes]);

  const contextValue = useMemo(
    () => ({
      quotes,
      addQuote,
      removeQuoteById,
      requestStatus,
      clearComparedQuotes,
    }),
    [quotes, addQuote, removeQuoteById, requestStatus, clearComparedQuotes]
  );

  return (
    <QuoteCompareContext.Provider value={contextValue}>
      {" "}
      {children}
    </QuoteCompareContext.Provider>
  );
};

export { QuoteCompareContext, QuoteCompareProvider };
